import { GeoService, getAdditionalHeaders, getGeoService, oldConsole, SearchRequest, SearchResponse, SearchTaskResponse } from "@esignal/polygon-service-api";
import { isUseIDBCache } from "./USE_IDB";
import { CachedObjectFn } from "../utils/CachedObject";
import { ServiceRequestMessage } from "./ServiceRequestMessage";
import { ReqPromise } from "./ReqPromise";

const LOUDSPEAKER_EMOJI = String.fromCodePoint(0x1f4e2); // 📢 encoded @see https://stackoverflow.com/questions/48419167/how-to-convert-one-emoji-character-to-unicode-codepoint-number-in-javascript

export class GeoServiceSwwProxy implements GeoService {
	private myWorker: SharedWorker;
	private reqMapCnt: number = 1;
	private reqMap: Map<number, ReqPromise> = new Map();

	constructor() {
		// or customize the chunk name with magic comments
		// see https://webpack.js.org/api/module-methods/#magic-comments
		// npm i ts-jest-mock-import-meta --save-dev # and uncomment transform in jest.config.js so it does not fail in jest
		this.myWorker = new SharedWorker(/* webpackChunkName: "sww" */ new URL("./entry/workerEntry", import.meta.url));

		this.myWorker.port.addEventListener("message", this.onMessage);
		this.myWorker.port.addEventListener("messageerror", this.onMessageError);

		window.addEventListener("beforeunload", this.onBeforeUnload);
		this.myWorker.port.start();
		this.configureIDBCache();
	}

	private onBeforeUnload = (): void => {
		this.myWorker.port.postMessage({ type: "onbeforeunload" });
	};

	private configureIDBCache = () => {
		this.postMessage({
			reqId: undefined,
			method: "configureUseIDBCache",
			params: [isUseIDBCache()],
			additionalHeaders: getAdditionalHeaders(),
		});
	};

	private postMessage = (message: ServiceRequestMessage<any>) => {
		this.myWorker.port.postMessage(message);
		// if (logger.isDebugEnabled()) {
		// 	logger.debug(`posting ${JSON.stringify(message)}`);
		// }
	};

	private onMessageError = (e: MessageEvent) => {
		console.error("onMessageError");
		console.error("onMessageError" + e);
	};

	private onMessage = (e: MessageEvent) => {
		const pkg = e.data as any;
		// if (console.isDebugEnabled()) {
		// 	logger.debug("receiving package", JSON.stringify(pkg));
		// }

		const { forReqId, type } = pkg;

		if (type === "log") {
			const { method, args, isFirst } = pkg;
			(isFirst ? console : oldConsole)[method].apply(console, [`swwb${LOUDSPEAKER_EMOJI}`, `isFirst:${isFirst}`, ...args]);
			return;
		}

		let { result } = pkg;

		if (result && Object.hasOwn(result, "__wrappedValue")) {
			// todo __wrappedValue is not used at all?
			result = result.__wrappedValue;
		}
		if (!forReqId) {
			console.error("Missing forReqId in " + pkg);
		} else {
			const entry = this.reqMap.get(forReqId);
			if (!entry) {
				console.error("Cannot find entry for reqId " + forReqId);
			} else {
				entry.resolve(result);
				this.reqMap.delete(forReqId);
			}
		}
	};

	private createReqPromise(): ReqPromise {
		const reqId = this.reqMapCnt++;
		const result = new ReqPromise(reqId);
		this.reqMap.set(reqId, result);
		return result;
	}

	private postReqMethodCall(method: string, params: any[]) {
		const req = this.createReqPromise();
		this.myWorker.port.postMessage({
			reqId: req.reqId,
			method,
			params,
		});
		return req.promise;
	}

	private createPostToSWWMethod =
		(methodName: string) =>
		(...args: any[]): Promise<any> => {
			const result = this.postReqMethodCall(methodName, args);
			return result;
		};

	public initialize(): void {
		this.myWorker.port.postMessage({
			type: "setAdditionalHeaders",
			headers: getAdditionalHeaders(),
		});
	}

	public version = this.createPostToSWWMethod("version"); // todo  use CachedObjectFn or always ask SWW
	public status = this.createPostToSWWMethod("status"); // todo  use CachedObjectFn or always ask SWW
	public search = this.createPostToSWWMethod("search");
	public search_list = this.createPostToSWWMethod("search_list");
	public search_task = this.createPostToSWWMethod("search_task");
	public search_task_create = this.createPostToSWWMethod("search_task_create");
}
